import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ConflictErrorCode,
	type FileConfigFieldError,
	FileConfigFieldErrorCode,
	type SftpConnectorListDTO,
	ValidationErrorCode,
} from '@today/types'
import { isAxiosError } from 'axios'
import { useState } from 'react'
import { type SftpConnectorDraft, sftpConnectorService } from '@/services/sftp-connector.service'

interface ApiErrorBody {
	error?: { code?: string; details?: { fields?: FileConfigFieldError[]; usedBy?: string[] } }
}

const bodyOf = (error: unknown) => (isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error : undefined)

const FIELD_MESSAGES: Record<string, string> = {
	[FileConfigFieldErrorCode.REQUIRED]: 'Required.',
	[FileConfigFieldErrorCode.INVALID_FORMAT]: 'Not in the expected format.',
	[FileConfigFieldErrorCode.OUT_OF_RANGE]: 'Out of the allowed range (1–65535).',
	[FileConfigFieldErrorCode.INVALID_TYPE]: 'Wrong kind of value.',
}

/** A field refusal's message; the host's INVALID_VALUE is a private or local address. */
const fieldMessage = ({ path, code }: FileConfigFieldError): string =>
	path === 'host' && code === FileConfigFieldErrorCode.INVALID_VALUE
		? 'Not allowed: use the public name or IP address of the SFTP server.'
		: (FIELD_MESSAGES[code] ?? 'Not accepted.')

/** A refused add, as the form shows it. */
export interface AddFailure {
	fields: Record<string, string>
	message: string | null
}

export const addFailureOf = (error: unknown): AddFailure => {
	const body = bodyOf(error)
	if (body?.code === ValidationErrorCode.CONNECTOR_INVALID) {
		const fields = Object.fromEntries((body.details?.fields ?? []).map((f) => [f.path, fieldMessage(f)]))
		return { fields, message: 'Some fields need attention.' }
	}
	if (body?.code === ConflictErrorCode.CONNECTOR_NAME_TAKEN) {
		return { fields: { name: 'Another connection already has this name.' }, message: null }
	}
	if (body?.code === ConflictErrorCode.CONNECTOR_LIMIT_REACHED) {
		return { fields: {}, message: 'This tenant already has the maximum of 5 connections.' }
	}
	return { fields: {}, message: 'The connection could not be added — try again.' }
}

/** A refused test, trust or delete, as its card shows it. */
export const actionFailureOf = (error: unknown): string => {
	const body = bodyOf(error)
	switch (body?.code) {
		case ConflictErrorCode.CONNECTOR_IN_USE:
			return `In use by: ${(body.details?.usedBy ?? []).join(', ')}`
		case ConflictErrorCode.HOST_KEY_CHANGED:
		case ConflictErrorCode.HOST_KEY_NOT_OBSERVED:
			return 'The server key changed since the last test — test the connection again.'
		case ConflictErrorCode.HOST_KEY_NOT_EXPECTED:
			return 'This key is not one of the pinned fingerprints.'
		default:
			return 'The request failed — try again.'
	}
}

/** Per-connector refusal messages, cleared when the connector is acted on again. */
function useCardErrors() {
	const [cardErrors, setCardErrors] = useState<Record<string, string>>({})
	return {
		cardErrors,
		clear: (id: string) => setCardErrors(({ [id]: _cleared, ...rest }) => rest),
		onError: (id: string) => (error: unknown) =>
			setCardErrors((current) => ({ ...current, [id]: actionFailureOf(error) })),
	}
}

/** The test, trust and delete of one connector, each refetching the list once stored. */
function useConnectorActions(tenantId: string, refresh: () => Promise<void>) {
	const errors = useCardErrors()
	const test = useMutation({ mutationFn: (id: string) => sftpConnectorService.test(tenantId, id), onSuccess: refresh })
	const trust = useMutation({
		mutationFn: ({ id, fingerprint }: { id: string; fingerprint: string }) =>
			sftpConnectorService.confirmHostKey(tenantId, id, fingerprint),
		onSuccess: refresh,
	})
	const remove = useMutation({
		mutationFn: (id: string) => sftpConnectorService.remove(tenantId, id),
		onSuccess: refresh,
	})
	return {
		cardErrors: errors.cardErrors,
		pending: test.isPending || trust.isPending || remove.isPending,
		test: (id: string) => {
			errors.clear(id)
			test.mutate(id, { onError: errors.onError(id) })
		},
		trust: (id: string, fingerprint: string) => {
			errors.clear(id)
			trust.mutate({ id, fingerprint }, { onError: errors.onError(id) })
		},
		remove: (id: string) => {
			errors.clear(id)
			remove.mutate(id, { onError: errors.onError(id) })
		},
	}
}

/**
 * A tenant's SFTP connections and the actions on them (TO-393 REQ-014) — for
 * the platform plane only: `enabled: false` makes no request at all. Every
 * stored change refetches the list.
 */
export function useSftpConnectors(tenantId: string, enabled: boolean) {
	const client = useQueryClient()
	const queryKey = ['sftp-connectors', tenantId] as const
	const query = useQuery<SftpConnectorListDTO>({
		queryKey,
		queryFn: () => sftpConnectorService.list(tenantId),
		enabled,
	})
	const [adding, setAdding] = useState(false)
	const refresh = () => client.invalidateQueries({ queryKey })
	const actions = useConnectorActions(tenantId, refresh)
	const create = useMutation({
		mutationFn: (draft: SftpConnectorDraft) => sftpConnectorService.create(tenantId, draft),
		onSuccess: () => {
			setAdding(false)
			return refresh()
		},
	})
	const failure = create.isError ? addFailureOf(create.error) : null
	return {
		...actions,
		connectors: query.data?.items ?? [],
		loadFailed: query.isError,
		adding,
		setAdding: (open: boolean) => {
			create.reset()
			setAdding(open)
		},
		busy: create.isPending || actions.pending,
		addErrors: failure?.fields ?? {},
		addError: failure?.message ?? null,
		add: (draft: SftpConnectorDraft) => create.mutate(draft),
	}
}
